import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, ChevronDown } from "lucide-react";
import { CtaButton, MaskedLines, Overline, Reveal, Section } from "./ui";

const faqs = [
  ["Como eu acesso?", "Você entra pelo navegador usando celular ou computador."],
  ["É um curso em vídeo?", "Não. É um mini app interativo que recebe uma situação e entrega uma missão."],
  ["Quanto tempo leva para receber uma missão?", "Poucos instantes. A proposta é colocar você em ação sem formulários longos."],
  ["Preciso de experiência com gestão?", "Não. Você informa o que está acontecendo e segue a missão apresentada."],
  ["Funciona no celular?", "Sim. O uso principal foi pensado para acontecer pelo celular."],
  ["Precisa acessar minha agenda?", "Não. Você informa se ela está cheia, saudável, fraca ou vazia."],
  ["E se eu tiver apenas 15 minutos?", "Informe o tempo disponível. O sistema escolhe uma missão compatível com aquela janela."],
  ["Ele substitui um sistema de gestão?", "Não. O MOM resolve a próxima decisão operacional, não toda a gestão do salão."],
  ["E se meu problema for mais complexo?", "Algumas situações exigem mais do que uma missão rápida. Quando isso acontecer, o MOM deixa claro que aquele problema precisa de uma análise mais profunda."],
];

export default function GuaranteeFaq() {
  const [open, setOpen] = useState(0);

  return (
    <>
      <Section tone="white">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <Overline>Risco zero</Overline>
            <MaskedLines
              inView
              as="h2"
              className="mt-5 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-hc-ink sm:text-4xl lg:text-5xl"
              lines={["Garantia incondicional", "de 7 dias"]}
            />
            <Reveal delay={0.15} className="mt-8 space-y-4 font-dmsans text-sm leading-relaxed text-hc-mutedInk sm:text-base">
              <p>
                Teste o MOM durante 7 dias. Se usar e sentir que não serve
                para sua rotina, devolvemos seu dinheiro.
              </p>
              <p className="font-serif text-xl font-medium italic text-hc-ink sm:text-2xl">
                Sem perguntas difíceis. Sem burocracia.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-10">
              <CtaButton testId="guarantee-cta" variant="gradient">Testar o MOM sem risco</CtaButton>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative mx-auto flex aspect-square w-full max-w-sm items-center justify-center rounded-full bg-white shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
            <div className="flex h-2/3 w-2/3 flex-col items-center justify-center rounded-full bg-mom-cream text-center">
              <ShieldCheck className="h-10 w-10 text-mom-vinho" />
              <p className="mt-4 font-serif text-5xl font-semibold text-hc-ink">7 dias</p>
              <p className="mt-1 font-dmsans text-[11px] font-semibold uppercase tracking-[0.2em] text-hc-mutedInk">
                para testar tudo
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section id="faq" tone="white" className="pt-0 lg:pt-0">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <Overline>Perguntas frequentes</Overline>
            <MaskedLines
              inView
              as="h2"
              className="mx-auto mt-5 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-hc-ink sm:text-4xl"
              lines={["Antes de decidir, tire a dúvida"]}
            />
          </div>
          <div className="mt-14 space-y-3">
            {faqs.map(([q, a], i) => {
              const isOpen = open === i;
              return (
                <Reveal key={i} delay={i * 0.04} y={16} className={`rounded-2xl transition-colors duration-300 ${isOpen ? "bg-white shadow-[0_16px_40px_rgba(0,0,0,0.07)]" : "bg-white/60 hover:bg-white"}`}>
                  <button
                    data-testid={`faq-item-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left"
                  >
                    <span className={`font-serif text-base font-medium transition-colors duration-300 sm:text-lg ${isOpen ? "text-mom-vinho" : "text-hc-ink/85"}`}>
                      {q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isOpen ? "bg-gradient-to-br from-mom-vinho to-mom-carmim text-white" : "bg-hc-off text-hc-ink"}`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 font-dmsans text-sm leading-relaxed text-hc-mutedInk sm:text-base">
                          {a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>
    </>
  );
}
